/**
 * Schémas Zod — Gating policies (Chunk 9.H.5 — D11 minimum Couche 2).
 *
 * Source DB : `app.gating_policies` (migration 0034).
 *
 * Couvre uniquement la Couche 2 minimum viable de l'ADR 0022 §2.5 :
 * block-or-allow simple sur les actions write (`assertHumanGated`). Couche 1
 * (scope `*.client_facing`) et Couche 3 (trigger PG `BEFORE UPDATE`) restent
 * hors scope tant que D34 (`app.tasks`) n'est pas livré.
 *
 * Convention `action_key` : dot notation `<resource>.<action>` (parité ADR
 * 0022 §2.5). Évite la confusion avec `<resource>:<action>` du scope api_key
 * (qui sépare resource du verbe via `:`).
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Action keys (enum strict typé compile-time — miroir des seeds DB)
// -----------------------------------------------------------------------------
//
// Cette liste DOIT rester en sync avec les default policies seedées dans
// `supabase/migrations/0034_gating_policies_and_audit_enriched.sql`.
//
// Ajouter une action_key = ajouter une ligne ici + une seed default policy
// dans une nouvelle migration (jamais modifier 0034 rétroactivement).

export const GATING_ACTION_KEYS = [
  "form_scenario.create",
  "form_scenario.share",
  "form_scenario.apply",
  // 9.G.1/9.G.3 — intake event terminal transitions
  "intake_event.resolve",
  "intake_event.reject",
  // 9.B — extraction LLM (coût)
  "extractions.run",
  // 9.AA.0 — gate humain-only des passes d'extraction MÉTIER (primes, vie_dma,
  // amc, dentaire, invalidite, conditions), seedé migration 0064
  // `agent_allowed = false`. Distinct de `extractions.run` ci-dessus, qui reste
  // ouvert aux agents pour la seule passe `identification` : celle-ci ne lit que
  // la structure du document. Cf. décision PO 9.AA.1 §3 — un agent ne doit ni
  // déclencher une passe métier, ni engager la dépense LLM correspondante.
  "extraction.business_pass",
  // ADR 0022 §2.5 — client-facing engageantes futures (pré-set seeded)
  "quote_request.send",
  // 9.I.1 — MI fulfill (pré-set seedée 9.H.5)
  "missing_information.fulfill",
  // 9.I.2 — MI secondary actions (waive / reopen / supersede)
  // Defaults seedés via migration 0035 (all `requires_human=true, agent_allowed=false`).
  "missing_information.waive",
  "missing_information.reopen",
  "missing_information.supersede",
  // ADR 0022 §2.5 — document_links create (pré-set seeded 9.H.5)
  "document_link.create",
  // 9.J — document_links delete (seedée via migration 0036, default
  // `agent_allowed=false` car action destructive parité MI waive/reopen/supersede 9.I.2).
  "document_link.delete",
  // 9.L.7 — quote_request cancel (seedée via migration 0037, default
  // `agent_allowed=false` car action destructive/engageante parité 9.J
  // delete + ADR 0023 §6 "cancel/expirer sans gating" ré-ouvert et tranché
  // PO 9.L.7 = gating actif pour préserver l'asymétrie create/cancel
  // sur la même resource family quote_request).
  "quote_request.cancel",
  // 9.T.4 — intake_event AI classifier + materialize (seedées via migration
  // 0045, default `agent_allowed=false` Phase 1). PO push-back explicite
  // contre `agent_allowed=true` proposé par audit initial :
  //   - classify   : coût LLM + risque hallucination cross-tenant ; Phase 1
  //                  human-auth broker only. Override tenant possible si
  //                  workflow agent externe émerge (rare).
  //   - materialize: création tenant-scoped d'entités (company/project) =
  //                  engagement structurel humain only Phase 1. Parité
  //                  `document_link.delete` / `quote_request.cancel` (0036/
  //                  0037) — asymétrie create-via-materialize vs create-
  //                  direct-via-POST-companies (dernière = `agent_allowed=true`
  //                  car appelée depuis wizard broker direct).
  "intake_event.classify",
  "intake_event.materialize",
  // 9.T.5.v2.2 — 3 actions humain-only post-materialize (seedées via migration
  // 0046, default `agent_allowed=false` Phase 1). PO Q3 v2.0 : chacune est
  // une décision structurelle sur la direction commerciale du document
  // (insurer_response / advisor_document / unknown) et engage un flux métier
  // dédié (submission / quote_request / attach-only). MCP wrapper hors scope
  // Phase 1 — feature-flag agent-mode arrivera plus tard.
  "intake_event.create_submission_from_file",
  "intake_event.create_quote_request_from_file",
  "intake_event.attach_project_only",
  // 9.T.5.v3.2 — extraction structurée sur un intake_event (pass_id=intake_extract_details).
  // Seedée via migration 0048, default `agent_allowed=false` humain-only Phase 1
  // (parité intake_event.classify / materialize + `create_*_from_file`). Override
  // tenant possible via INSERT tenant-scoped `gating_policies` — pattern éprouvé
  // depuis 0034. Distincte de `extractions.run` submission (0034:146) qui reste
  // `agent_allowed=true` — les 2 mondes ont des politiques de gating distinctes.
  "intake_event.extract_details",
  // 9.T.5.v3.7 — mise à jour partielle d'un recipient existant
  // (`recipient_email` / `recipient_contact_id` / `language`) via
  // `PATCH /v1/quote-requests/:id/recipients/:recipientId`.
  // Seedée via migration 0049, default `agent_allowed=false` humain-only Phase 1.
  // Rationale (Q2 PO 2026-07-15) : modifier QUI reçoit l'engagement externe
  // partage la sémantique client-facing engageante de `.send` (0034). Une clé
  // dédiée (plutôt que réutiliser `.send` ou introduire un `quote_request.update`
  // générique) rend explicite le fait que ce PATCH est pré-requis à l'envoi
  // externe. Distincte de `PATCH /v1/quote-requests/:id` (title/date) qui reste
  // non-gaté car sans surface externe. Override tenant possible.
  "quote_request.update_recipient",
  // F2-Forms-1a — 8 actions moteur formulaires versionnés, seedées via migration
  // 0055 (default `agent_allowed=false` humain-only Phase 1). MVP broker-only ;
  // MCP/agent hors scope (Phase 3). Consommées par F2-Forms-1b-i (templates) et
  // 1b-ii (réponses).
  "form_template.create",
  "form_template.update",
  "form_template.publish",
  "form_template.archive",
  // 9.BJ.1 — le geste inverse a la même portée, donc la même garde.
  "form_template.restore",
  // 9.U.3 — génération depuis document (seedée par la migration 0059,
  // `agent_allowed=false` : aucun canal machine ouvert par ce chunk).
  "form_template.generate_from_document",
  "form_response.create",
  "form_response.update",
  "form_response.submit",
  "form_response.lock",
  // 9.W.2 — abandon logique d'un brouillon conseiller
  // (`DELETE /v1/form-responses/:id`, soft-delete). Seedée par la migration 0062,
  // `agent_allowed=false` : aucun canal machine n'a de raison de supprimer le
  // travail d'un humain.
  "form_response.discard",
] as const;

export const GatingActionKeySchema = z.enum(GATING_ACTION_KEYS);
export type GatingActionKey = z.infer<typeof GatingActionKeySchema>;

// -----------------------------------------------------------------------------
// Outcome — issue d'un appel `assertHumanGated()`
// -----------------------------------------------------------------------------

export const GatingOutcomeSchema = z.enum(["allowed", "blocked"]);
export type GatingOutcome = z.infer<typeof GatingOutcomeSchema>;

// -----------------------------------------------------------------------------
// Gating decision — shape persistée dans `app.audit_log.gating_decision`
// -----------------------------------------------------------------------------
//
// Construit côté handler par `assertHumanGated()` ; sérialisé en JSON et
// SET LOCAL dans le GUC `app.gating_decision_json` ; lu par le trigger
// `app.audit_trigger()` qui INSERT dans `audit_log.gating_decision`.

export const GatingDecisionSchema = z.object({
  action_key: GatingActionKeySchema,
  policy_id: UuidSchema,
  /** NULL = policy globale Kollectivia (fallback). */
  policy_tenant_id: UuidSchema.nullable(),
  requires_human: z.boolean(),
  agent_allowed: z.boolean(),
  outcome: GatingOutcomeSchema,
  decided_at: z.string().datetime(),
});
export type GatingDecision = z.infer<typeof GatingDecisionSchema>;

// -----------------------------------------------------------------------------
// Row (lecture future API admin) — pas exposé pour l'instant (super_admin SQL)
// -----------------------------------------------------------------------------
//
// Phase 1 : aucune route API publique pour CRUD gating_policies (PO-Q3).
// Schema fourni quand même pour un futur usage admin endpoint sans recréation.

export const GatingPolicyRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema.nullable(),
  action_key: GatingActionKeySchema,
  requires_human: z.boolean(),
  agent_allowed: z.boolean(),
  notes: z.string().nullable(),
  created_by: UuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  superseded_by: UuidSchema.nullable(),
  superseded_at: z.string().nullable(),
  deleted_at: z.string().nullable(),
});
export type GatingPolicyRow = z.infer<typeof GatingPolicyRowSchema>;
